import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { getMe, login as apiLogin, logout as apiLogout } from './api';

export type UserRole = 'admin' | 'sales' | 'customer';

interface User {
  id: string;
  email: string;
  role: UserRole;
  assigned_chatbot_id: string | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      const result = await getMe();
      if (result.success && result.data) {
        setUser({
          ...result.data,
          role: result.data.role as UserRole,
          assigned_chatbot_id: result.data.assigned_chatbot_id || null
        });
      }
    } catch (e) {
      // Not logged in
    } finally {
      setLoading(false);
    }
  }

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    const result = await apiLogin(email, password);
    if (result.success && result.data) {
      setUser({
        ...result.data,
        role: result.data.role as UserRole,
        assigned_chatbot_id: result.data.assigned_chatbot_id || null
      });
      return true;
    }
    return false;
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, loading, login, logout }), [user, loading, login, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
